// Finar 14.4 (K4) — hücre editöründe "Form yanıtını al": FORM rozetiyle aynı eylem
// (`POST /kpi/values/from-form`, varsayılan işletme + proje yılı), ama sessiz değil — onay satırı
// ister. Birim uyuşmazlığında (`unit_mismatch`) çağıran bu bloğu hiç render etmez. Aynı aile farklı
// ölçekte form değeri KPI birimine çevrilmiş haliyle gösterilir (sunucu da aynı çevrimi yapar).
import { useState } from "react";
import { convertUnit, formatKpiNumber, type KpiFormMismatch } from "@mahir/shared";
import { applyFormToKpi } from "../../api/kpi-checks";
import { KPI_CHECKS_COPY as C } from "../../lib/kpi-checks-copy";

interface Props {
  mismatch: KpiFormMismatch;
  /** Başarılı yazımdan sonra (grid yenile + editörü kapat). */
  onApplied: () => void;
  /** Kırılım toplamı (türetilmiş): eylem pasif, gerekçe düğme `title`'ında (spec §6.2) */
  disabledReason?: string | undefined;
}

function withUnit(value: number, unit: string | null): string {
  return `${formatKpiNumber(value)}${unit ? ` ${unit}` : ""}`;
}

export function KpiCellEditorFormLink({
  mismatch,
  onApplied,
  disabledReason,
}: Props): JSX.Element | null {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (mismatch.formValue === null || mismatch.kind === "unit_mismatch") return null;
  const formText = withUnit(mismatch.formValue, mismatch.questionUnit ?? mismatch.unit);
  const inKpi =
    mismatch.converted && mismatch.questionUnit && mismatch.unit
      ? convertUnit(mismatch.formValue, mismatch.questionUnit, mismatch.unit)
      : mismatch.formValue;
  const kpiText = withUnit(inKpi ?? mismatch.formValue, mismatch.unit);

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await applyFormToKpi(mismatch.projectId, mismatch.questionCode);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : C.editorFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="kpi-editor-form-link"
      className="text-xs mt-2 px-2 py-1.5 rounded-lg flex flex-wrap items-center gap-2"
      style={{
        background: "var(--color-warning-bg)",
        border: "1px solid var(--color-warning-border, var(--color-border))",
        color: "var(--color-text)",
      }}
    >
      {confirming ? (
        <>
          <span>{C.editorConfirm(kpiText)}</span>
          <button
            type="button"
            className="underline font-medium"
            disabled={busy}
            onClick={() => void apply()}
          >
            {C.editorConfirmYes}
          </button>
          <button
            type="button"
            className="underline"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            {C.editorConfirmNo}
          </button>
        </>
      ) : (
        <>
          <span title={mismatch.questionLabel}>{C.editorFormValue(formText)}</span>
          <button
            type="button"
            className="underline font-medium"
            data-testid="kpi-editor-form-take"
            disabled={!!disabledReason}
            title={disabledReason}
            aria-disabled={disabledReason ? true : undefined}
            style={
              disabledReason ? { color: "var(--color-text-3)", cursor: "not-allowed" } : undefined
            }
            onClick={() => setConfirming(true)}
          >
            {C.editorTakeForm}
          </button>
          {mismatch.converted ? (
            <span style={{ color: "var(--color-text-2)" }}>{C.converted(formText, kpiText)}</span>
          ) : null}
        </>
      )}
      {error ? (
        <span role="alert" style={{ color: "var(--color-error)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
